import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { loginTo } from "@/components/landing/data";
import { Search, MapPin, ExternalLink, ArrowRight, LogIn } from "lucide-react";

function GuestHeader() {
  return (
    <header className="glass sticky top-0 z-50 border-b border-border">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="browse-logo" className="flex items-center">
          <img src="/spora1-logo.webp" alt="SPORA1" className="h-9 w-auto sm:h-10" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="browse-signin" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Sign in</Link>
          <Link to="/register" data-testid="browse-register" className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-all hover:bg-[#FF8533]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

function isSafeUrl(u) {
  if (!u) return null;
  try { const x = new URL(u); return (x.protocol === "http:" || x.protocol === "https:") ? x.href : null; } catch { return null; }
}

export default function BrowseDiscover() {
  const [cats, setCats] = useState(null);
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "facilities";
  const [q, setQ] = useState(params.get("sport") || "");

  useEffect(() => { api.get("/public/discover").then((r) => setCats(r.data.categories)).catch(() => setCats([])); }, []);

  const setActive = (k) => { const p = new URLSearchParams(params); p.set("cat", k); setParams(p, { replace: true }); };

  const current = cats?.find((c) => c.key === active) || cats?.[0];
  const items = (current?.items || []).filter((i) => !q || JSON.stringify(i).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <GuestHeader />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground"><span className="font-semibold">Browsing as a guest.</span> Explore freely - sign in only when you want to book or post.</p>
          <Link to={loginTo("/app/discover")} data-testid="browse-guest-signin" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[#FF8533]"><LogIn className="h-4 w-4" /> Sign in</Link>
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Discover</h1>
        <p className="mt-1 text-sm text-muted-foreground">Explore verified athletes, institutions, facilities, academies and more.</p>

        {!cats ? <div className="py-20"><Spinner /></div> : (
          <>
            <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
              {cats.map((c) => (
                <button key={c.key} data-testid={`browse-cat-${c.key}`} onClick={() => setActive(c.key)}
                  className={`flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${
                    c.key === active ? "border-primary bg-primary/15 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>
                  {c.label} <span className="rounded-full bg-secondary px-1.5 text-xs">{c.items.length}</span>
                </button>
              ))}
            </div>

            <div className="relative mt-4">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input data-testid="browse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${current?.label || ""}…`}
                className="w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary" />
            </div>

            {items.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">No {current?.label} listed yet.</div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((it, idx) => {
                  const title = it.display_name || it.name;
                  const sub = it.sport || it.category || it.type || it.level || "";
                  const loc = [it.city, it.state].filter(Boolean).join(", ");
                  const website = isSafeUrl(it.website);
                  const isFac = active === "facilities";
                  const Wrapper = isFac ? Link : "div";
                  const wp = isFac ? { to: `/browse/facilities/${it.id}`, "data-testid": `browse-facility-${it.id}` } : {};
                  return (
                    <Wrapper key={it.id || idx} {...wp}
                      className={`block rounded-2xl border border-border bg-card p-5 transition-all ${isFac ? "hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg" : ""}`}>
                      {it.image_url && <img src={it.image_url} alt={title} className="mb-3 h-36 w-full rounded-xl object-cover" />}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display text-lg font-semibold">{title}</h3>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {it.rating != null && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">★ {it.rating}</span>}
                          {active === "athletes" && it.open_to_join && (
                            <span data-testid={`browse-open-to-play-${it.id}`} className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Open to play</span>
                          )}
                        </div>
                      </div>
                      {sub && <p className="mt-0.5 text-sm capitalize text-primary">{sub}</p>}
                      {(it.description || it.bio) && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{it.description || it.bio}</p>}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        {loc && <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {loc}</p>}
                        {isFac ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">View & book <ArrowRight className="h-3 w-3" /></span>
                          : website ? <a href={website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">Website <ExternalLink className="h-3 w-3" /></a> : null}
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
